// src/pages/SakshamBiharHome.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

// Inline SVG for Indian Flag
const IndianFlagSVG = ({ className = "inline h-4 w-6" }) => (
  <svg
    className={className}
    viewBox="0 0 16 12"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect y="0" width="16" height="4" fill="#FF9933" />
    <rect y="4" width="16" height="4" fill="white" />
    <circle cx="8" cy="6" r="1.5" fill="#006BA9" stroke="white" strokeWidth="0.1" />
    <rect y="8" width="16" height="4" fill="#138808" />
  </svg>
);

const SakshamBiharHome = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLogged = authState?.isLogged || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/sakshambihar");
  };

  const groupNames = [
    { label: "Bihar Books", emoji: "📖", link: "/bihar-bookworld" },        
    { label: "SAKSHAM Bihar MSME", emoji: "🏢", link: "/msme" },
    { label: "Bihar Krishi & Gaon", emoji: "🌾🏘️", link: "/agrilink" },
    { label: "Bihar Manufacturing", emoji: "🏭", link: "/syntramfg" },
    { label: "Bihar Startup", emoji: "🚀", link: "/startup" },
    { label: "SAKSHAM Shakti", emoji: "🏡", link: "/sheearns" },
    { label: "Jeevika", emoji: "👩‍🎓", link: "/sheearns" },
    { label: "Bihar Yuvasolver", emoji: "🔥", link: "/Yuvasolver" },
    { label: "Bihar Teenagers", emoji: "📱", link: "/Golden7" },
    { label: "SAKSHAM Bihari Shramik", emoji: "💪", link: "/sakshamshram" },
    { label: "Teachanble", emoji: "👩‍🏫", link: "/teachable" },
    { label: "Bihar Learnings & Skills", emoji: "📖✨", link: "/lnd" },    
    { label: "Bihar Government Efficiency", emoji: "🏢", link: "/sheetal" },
    { label: "Responsible Bihari Citizen", emoji: "👥", link: "/rim" },
    { label: "Bihar Museum", emoji: "🏛️", link: "/museum" },
    { label: "Bihar Bazar", emoji: "🛒", link: "/khareedo" },   
    { label: "Bihar Civic Index", emoji: "🏙️", link: "/biharcivicindex" },   
  ];

  return (
    <div className="min-h-screen flex flex-col bg-green-950 text-emerald-100 font-sans">
      <header className="sticky top-0 z-50 bg-black px-4 py-1 flex items-center justify-between shadow-lg">
        <div className="flex flex-col">
          <div className="text-xl lg:text-2xl font-bold text-yellow-200 flex items-center gap-1">
            <IndianFlagSVG /> B-FORCE
          </div>
          <div className="text-[11px] text-gray-200">
            Bihar Foundations, Outcomes, Resilience, Capability, Empowerment
          </div>
          <div className="text-[11px] text-cyan-400">
            From the land of learning to the engine of the digital future - Bihar rises again.
          </div>          
        </div>
        <div className="hidden md:flex items-center gap-3">
          {isLogged ? (
            <>
              <div className="text-xs text-white">{userName}</div>
              <button
                onClick={handleLogout}
                className="w-16 p-2 rounded-md bg-red-600 hover:bg-red-700 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="w-16 px-3 py-1 rounded-full border border-white text-sm hover:bg-white/10"
              >
                🔐
              </button>
            </>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-xl lg:text-2xl text-white"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-orange-900 border-b border-orange-800 px-4 py-3 animate-[fadeIn_0.3s_ease-in-out]">
          {isLogged ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-xs text-white font-medium flex items-center gap-1">
                {userName} <IndianFlagSVG className="h-3 w-4" />
              </div>
              <button
                onClick={handleLogout}
                className="w-16 p-2 rounded-md bg-red-600 hover:bg-red-700"
              >
                ⏻
              </button>
            </div>
          ) : (
            <div className="flex justify-center gap-6">
              <Link to="/login" className="w-16 text-white">🔐</Link>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-col h-1 bg-gradient-to-r from-orange-500 via-white to-emerald-500 shadow-lg"></div>

      <div className="bg-gray-800 text-[11px] lg:text-sm py-2 px-2 text-center text-orange-300 leading-relaxed">
        <span className="text-amber-400 font-semibold">🌏 Transformative Framework:</span> Empowering 
        <span className="text-emerald-300 font-semibold ml-2">1.3M+ Youth & Women Jobs</span> • 
        <span className="text-amber-300"> 💰 $25B GDP Boost</span> • 
        <span className="text-emerald-300"> 📈 ₹18,000.00 Per Capita Rise</span> • 
        <span className="text-orange-400">🏢 20 lakh MSMEs</span> • 
        <span className="text-green-300">🌾 1.5 crore Farmers</span> • 
        <span className="text-blue-300">⚙️ 2.8 crore Workers</span> • 
        <span className="text-pink-300">👩‍💼 6.0 crore Women</span> • 
        <span className="text-yellow-300">👩‍🏫 10 lakh+ Teachers</span> • 
        <span className="text-teal-300">🎓 4.0 crore Students</span> • 
        <span className="text-orange-300">🏛️ Govt, PSUs & Local Bodies Coordinated</span>. 
        <span className="text-emerald-400 font-semibold">⚡ Mission-Mode Implementation Ahead!</span>
        <span className="text-sky-400 font-semibold">🌍 Empowering Bihar with innovation & global support</span>
      </div>

      <div className="mt-1 mb-1 px-3 py-2 border-b border-emerald-700 bg-black">
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-1 place-items-center">
          {groupNames.map((group) => (
            <button
              key={group.label}
              onClick={() => navigate(group.link)}
              className="flex flex-col items-center justify-center w-full px-2 py-2 rounded-md text-xs lg:text-sm font-medium transition bg-emerald-800/50 border border-white/20 hover:bg-emerald-700/70"
            >
              <span className="text-lg">{group.emoji}</span>
              <span className="mt-1 text-center">{group.label}</span>
            </button>
          ))}
        </div>
      </div>

      <section className="relative bg-gradient-to-br from-orange-950 via-gray-950 to-emerald-950 py-2 px-3 flex flex-col items-center justify-center text-center overflow-hidden">
        <div className="relative z-10 w-full animate-[fadeInUp_0.8s_ease-out] px-2">
          <div className="text-center px-3">
            <p className="text-xs lg:text-sm font-serif italic text-emerald-200/90 mb-1 leading-relaxed tracking-wide flex flex-col items-center justify-center">
              <span className="text-center">
                B-FORCE <span className="text-cyan-400 font-medium">(Bihar Framework for Outcomes, Resilience, Capability & Empowerment): </span> Bihar’s integrated digital transformation engine combining citizens, enterprises, governance, deep-tech and culture into a single outcome-oriented framework.  
                A multi-sector, citizen-centric, deep-tech enabled transformation framework, <span className="text-amber-300"> not a reskilling scheme.</span>  
              </span>
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {[
              { icon: "🚀", label: "Viksit Bihar 2047" },
              { icon: <IndianFlagSVG className="h-4 w-6" />, label: "Atmanirbhar Bihar" },
              { icon: "🏭", label: "Make In Bihar" },
              { icon: "💻", label: "Digital Bihar" },
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-emerald-900/30 backdrop-blur-sm rounded-lg p-2 border border-orange-500/30 flex flex-col items-center justify-center text-center hover:scale-105 transition-all"
              >
                <span className="text-lg lg:text-xl mb-1">{item.icon}</span>
                <span className="text-[10px] lg:text-sm font-semibold text-emerald-100">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
          <button className="mt-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-emerald-500 text-white font-semibold rounded-full shadow-lg hover:scale-105 transition">
            Join the SAKSHAM Bihar Revolution <IndianFlagSVG className="h-4 w-5 inline ml-1" />
          </button>
        </div>
        <div className="bg-white/5 text-xs lg:text-sm py-2 text-center text-orange-300">
          🔒 Trusted & Secure Platform – ISO 9001, ISO 27001, SOC-2 Certified
        </div>
      </section>

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SakshamBiharHome;
